import { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import EDDialog from '../../../../../../../BaseComponents/Dialog/EDDialog';
import EDBox from '../../../../../../../BaseComponents/EDBox/EDBox';
import EDTypography from '../../../../../../../BaseComponents/Typography/EDTypography';
import EDCheckbox from '../../../../../../../BaseComponents/Checkboxes/EDCheckbox';
import EDList from '../../../../../../../BaseComponents/List/EDList';
import { IBusinessDataFormContext } from '../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/BusinessDataForm';
import {
  UpdateRepeteraAktivitetQuery,
  UpdateRepeteraAktivitetQueryVariables,
} from '../../Queries/_QueryTypes/UpdateRepeteraAktivitetQuery';
import updateRepeteraAktivitetQuery from '../../Queries/UpdateRepeteraAktivitetQuery.gql';
import { useMDDetailSelectionContext } from '../../../../../../../Components/MasterDetail/Contexts/MdDetailSelectionContext';
import { useDeleteMutation } from '../../../../../../../Components/ObjectMenu/DeleteMutation';
import {
  deleteRepAkt,
  deleteRepAktVariables,
} from '../../Mutations/_QueryTypes/deleteRepAkt';
import deleteRepeteraAktivitetMutation from '../../../Aktivitet/Mutations/DeleteRepeteradAktivitetMutation.gql';
import EDIcon from '../../../../../../../BaseComponents/Icon/EDIcon';
import { makeStyles } from 'tss-react/mui';
import { Theme } from '@mui/material';
import EDAvenyProgress from '../../../../../../../BaseComponents/ProgressIndicators/AvenyProgress/EDAvenyProgress';
import { useIsSmallScreen } from '../../../../../../../Hooks/useFullscreenView';

const useStyles = makeStyles()(
  (theme: Theme) =>
    ({
      topArea: {
        alignItems: 'center',
        justifyContent: 'flex-start',
      },
    } as const)
);

interface IProps {
  formContext: IBusinessDataFormContext<any, unknown>;
  openDeleteRepeteradeAktivitetDialog: boolean;
  setOpenDeleteRepeteradeAktivitetDialog: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  aktivitetId: any;
  activeDatabase: string | undefined;
}

const DeleteRepeteradeAktivitetDialog: React.FC<
  React.PropsWithChildren<IProps>
> = props => {
  const [deleteAktivitet, setDeleteSingleAktivitet] = useState(true);
  const [allFutureAktivitiesChecked, setAllFutureActivitiesChecked] =
    useState(false);
  const { unselectDetail } = useMDDetailSelectionContext();
  const closeDialog = () => {
    props.setOpenDeleteRepeteradeAktivitetDialog(false);
  };
  const { classes } = useStyles();
  const isSmallScreen = useIsSmallScreen();

  const { data: antalRepeteradeAktiviteter_data } = useQuery<
    UpdateRepeteraAktivitetQuery,
    UpdateRepeteraAktivitetQueryVariables
  >(updateRepeteraAktivitetQuery, {
    variables: {
      id: props.aktivitetId.id,
    },
    skip: !props.openDeleteRepeteradeAktivitetDialog || !props.aktivitetId.id,
  });

  const [deleteRepAkt, { loading: isDeleting }] = useMutation<
    deleteRepAkt,
    deleteRepAktVariables
  >(deleteRepeteraAktivitetMutation, {
    refetchQueries: ['AktivitetQuery'],
  });

  const deleteSingleAktivity = () => {
    setDeleteSingleAktivitet(!deleteAktivitet);
    setAllFutureActivitiesChecked(false);
  };

  const deleteAllRepActivities = () => {
    setAllFutureActivitiesChecked(!allFutureAktivitiesChecked);
    setDeleteSingleAktivitet(false);
  };

  const deleteMutation = useDeleteMutation(
    {
      id: props.aktivitetId.id,
      collectionName: 'BOAktivitet',
      dbName: props.activeDatabase,
      version: props.formContext?.formState?.currentValues?._version,
    },
    'Delete'
  );

  const deleteAktivitetAndCloseDialog = async () => {
    if (deleteAktivitet) {
      deleteMutation();
    } else
      await deleteRepAkt({
        variables: {
          dbname: props.activeDatabase || '',
          aktId: props.aktivitetId.id,
        },
        refetchQueries: ['rtnWebKalenderMain'],
      });
    unselectDetail?.();
  };

  return (
    <EDDialog
      title="Ta bort aktivitet"
      open={props.openDeleteRepeteradeAktivitetDialog}
      onClose={closeDialog}
      footerActions={[
        {
          label: 'Avbryt',
          onClick: closeDialog,
        },
        {
          label: 'Ta bort',
          onClick: deleteAktivitetAndCloseDialog,
          buttonProps: {
            disabledReason: 'Du måste göra minst ett val',
            disabled:
              (!deleteAktivitet && !allFutureAktivitiesChecked) || isDeleting,
          },
        },
      ]}
      headerProps={{
        headerIcon: (
          <EDIcon
            marginRight={3}
            color="warning"
            iconType="warning"
            size="large"
          />
        ),
        classes: {
          topArea: classes.topArea,
        } as any,
      }}
      testId="deleteRepeteraDialog"
    >
      <EDBox sx={{ position: isSmallScreen ? 'static' : 'relative' }}>
        {isDeleting && <EDAvenyProgress overlay />}
        {antalRepeteradeAktiviteter_data?.node?.__typename ===
          'TEDBOAktivitet' && (
          <>
            {antalRepeteradeAktiviteter_data?.node?._Associations
              ?.RepeteradeAktiviteter_All &&
            antalRepeteradeAktiviteter_data?.node?._Associations?.RepeteradeAktiviteter_All?.filter(
              (x: any) => x.FRANDATUM > props.formContext?.data?.FRANDATUM
            )?.length > 0 ? (
              <>
                <EDTypography bold>
                  Denna aktivitet ingår i en repetition. Vill du ta bort:
                </EDTypography>
                <EDList>
                  <EDBox sx={{ display: 'flex', alignItems: 'center' }}>
                    <EDCheckbox
                      checked={deleteAktivitet}
                      onCheck={deleteSingleAktivity}
                    />

                    <EDTypography>Endast denna aktivitet</EDTypography>
                  </EDBox>

                  <EDBox sx={{ display: 'flex', alignItems: 'center' }}>
                    <EDCheckbox
                      checked={allFutureAktivitiesChecked}
                      onCheck={() => {
                        deleteAllRepActivities();
                      }}
                    />
                    <EDTypography>
                      Denna och alla framtida aktiviteter (
                      {
                        antalRepeteradeAktiviteter_data?.node?._Associations?.RepeteradeAktiviteter_All?.filter(
                          (x: any) =>
                            x.FRANDATUM > props.formContext?.data?.FRANDATUM
                        )?.length
                      }
                      )
                    </EDTypography>
                  </EDBox>
                </EDList>
              </>
            ) : (
              <>
                <EDTypography bold>
                  Denna aktivitet ingår i en repetition. Vill du ta bort:
                </EDTypography>
                <EDList>
                  <EDBox sx={{ display: 'flex', alignItems: 'center' }}>
                    <EDCheckbox
                      checked={deleteAktivitet}
                      onCheck={deleteSingleAktivity}
                    />

                    <EDTypography>Endast denna aktivitet</EDTypography>
                  </EDBox>
                </EDList>
              </>
            )}
          </>
        )}
      </EDBox>
    </EDDialog>
  );
};

export default DeleteRepeteradeAktivitetDialog;
